import { useState, useEffect } from 'react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { Plus, Search, Edit, Trash2, Upload, Download, Box, Barcode, Printer } from 'lucide-react';
import { BarcodeLabel, BarcodePrintView } from '../components/BarcodeLabel';

export default function ItemsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [categories, setCategories] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [barcodeDialogOpen, setBarcodeDialogOpen] = useState(false);
  const [selectedItemForBarcode, setSelectedItemForBarcode] = useState(null);
  const [showBatchPrint, setShowBatchPrint] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [formData, setFormData] = useState({
    sku: '',
    name: '',
    description: '',
    category: '',
    unit_of_measure: 'EA',
    barcode: '',
    cost_price: 0,
    selling_price: 0,
    initial_quantity: 0,
    reorder_point: 0,
    reorder_qty: 0,
    safety_stock: 0
  });

  useEffect(() => {
    fetchItems();
    fetchCategories();
  }, [searchQuery, categoryFilter]);

  const fetchItems = async () => {
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', searchQuery);
      if (categoryFilter && categoryFilter !== 'all') params.append('category', categoryFilter);
      
      const response = await api.get(`/items?${params.toString()}`);
      setItems(response.data);
    } catch (error) {
      toast.error('Failed to fetch items');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await api.get('/items/categories/list');
      setCategories(response.data);
    } catch (error) {
      console.error('Failed to fetch categories');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingItem) {
        const { set_quantity, initial_quantity, ...updateData } = formData;
        await api.put(`/items/${editingItem.id}`, updateData);
        // If user entered a stock quantity, set it
        if (set_quantity !== '' && set_quantity !== null && set_quantity !== undefined) {
          try {
            await api.post(`/items/${editingItem.id}/set-stock`, { quantity: parseInt(set_quantity) });
          } catch (stockErr) {
            toast.error(stockErr.response?.data?.detail || 'Item updated but stock set failed');
          }
        }
        toast.success('Item updated');
      } else {
        await api.post('/items', formData);
        toast.success('Item created');
      }
      setDialogOpen(false);
      resetForm();
      fetchItems();
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      sku: item.sku,
      name: item.name,
      description: item.description || '',
      category: item.category || '',
      unit_of_measure: item.unit_of_measure || 'EA',
      barcode: item.barcode || '',
      cost_price: item.cost_price || item.avg_cost || 0,
      selling_price: item.selling_price || 0,
      set_quantity: '',
      reorder_point: item.reorder_point || 0,
      reorder_qty: item.reorder_qty || 0,
      safety_stock: item.safety_stock || 0
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;
    try {
      await api.delete(`/items/${id}`);
      toast.success('Item deleted');
      fetchItems();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Delete failed');
    }
  };

  const resetForm = () => {
    setEditingItem(null);
    setFormData({
      sku: '',
      name: '',
      description: '',
      category: '',
      unit_of_measure: 'EA',
      barcode: '',
      cost_price: 0,
      selling_price: 0,
      initial_quantity: 0,
      reorder_point: 0,
      reorder_qty: 0,
      safety_stock: 0
    });
  };

  const handleExport = async () => {
    try {
      const response = await api.get('/export/items');
      const blob = new Blob([response.data.csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = response.data.filename;
      a.click();
      toast.success('Export complete');
    } catch (error) {
      toast.error('Export failed');
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await api.post('/import/items', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(`Imported ${response.data.imported} items`);
      if (response.data.errors?.length > 0) {
        toast.warning(`${response.data.errors.length} errors occurred`);
      }
      fetchItems();
      fetchCategories();
    } catch (error) {
      toast.error('Import failed');
    }
    e.target.value = '';
  };

  return (
    <div className="space-y-6" data-testid="items-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-heading-black text-3xl lg:text-4xl text-[#0A0A0A] tracking-tight">ITEMS</h1>
          <p className="text-[#0A0A0A]/60 mt-1">Manage your inventory items and SKUs</p>
        </div>
        <div className="flex items-center gap-2">
          {selectedItems.length > 0 && (
            <Button 
              variant="outline" 
              onClick={() => setShowBatchPrint(true)}
              className="border-green-500 text-green-600 hover:bg-green-50"
            >
              <Printer className="w-4 h-4 mr-2" />
              Print Barcodes ({selectedItems.length})
            </Button>
          )}
          <label htmlFor="import-file">
            <Button variant="outline" className="cursor-pointer" asChild>
              <span>
                <Upload className="w-4 h-4 mr-2" />
                Import
              </span>
            </Button>
            <input
              id="import-file"
              type="file"
              accept=".csv"
              onChange={handleImport}
              className="hidden"
            />
          </label>
          <Button variant="outline" onClick={handleExport} data-testid="export-items-btn">
            <Download className="w-4 h-4 mr-2" />
            Export
          </Button>
          <Button 
            className="bg-[#007AFF] hover:bg-[#0066D6]" 
            onClick={() => { resetForm(); setDialogOpen(true); }}
            data-testid="add-item-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Item
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="border border-[#E5E5EA] shadow-none">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#0A0A0A]/40" />
              <Input
                placeholder="Search by SKU, name, or barcode..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 border-[#E5E5EA]"
                data-testid="search-items-input"
              />
            </div>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-full sm:w-48 border-[#E5E5EA]" data-testid="category-filter">
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.filter(cat => cat && cat.trim()).map((cat) => (
                  <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Items Table */}
      <Card className="border border-[#E5E5EA] shadow-none">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#007AFF]"></div>
            </div>
          ) : items.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-center">
              <Box className="w-12 h-12 text-[#0A0A0A]/20 mb-4" />
              <p className="text-[#0A0A0A]/60">No items found</p>
              <Button 
                className="mt-4 bg-[#007AFF] hover:bg-[#0066D6]"
                onClick={() => { resetForm(); setDialogOpen(true); }}
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Your First Item
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-b border-[#E5E5EA]">
                  <TableHead className="w-10">
                    <input 
                      type="checkbox"
                      checked={selectedItems.length === items.length && items.length > 0}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedItems(items);
                        } else {
                          setSelectedItems([]);
                        }
                      }}
                      className="rounded border-gray-300"
                    />
                  </TableHead>
                  <TableHead className="text-overline text-[#0A0A0A]/50">SKU</TableHead>
                  <TableHead className="text-overline text-[#0A0A0A]/50">Name</TableHead>
                  <TableHead className="text-overline text-[#0A0A0A]/50">Category</TableHead>
                  <TableHead className="text-overline text-[#0A0A0A]/50 text-right">Stock</TableHead>
                  <TableHead className="text-overline text-[#0A0A0A]/50 text-right">Cost Price</TableHead>
                  <TableHead className="text-overline text-[#0A0A0A]/50 text-right">Selling Price</TableHead>
                  <TableHead className="text-overline text-[#0A0A0A]/50">Status</TableHead>
                  <TableHead className="text-overline text-[#0A0A0A]/50 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((item) => (
                  <TableRow key={item.id} className="border-b border-[#E5E5EA]" data-testid={`item-row-${item.sku}`}>
                    <TableCell>
                      <input 
                        type="checkbox"
                        checked={selectedItems.some(i => i.id === item.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedItems([...selectedItems, item]);
                          } else {
                            setSelectedItems(selectedItems.filter(i => i.id !== item.id));
                          }
                        }}
                        className="rounded border-gray-300"
                      />
                    </TableCell>
                    <TableCell className="font-mono text-sm">{item.sku}</TableCell>
                    <TableCell className="font-medium">{item.name}</TableCell>
                    <TableCell className="text-[#0A0A0A]/60">{item.category || '-'}</TableCell>
                    <TableCell className="text-right">
                      <span className={`font-mono font-semibold ${
                        (item.quantity_on_hand || 0) <= 0 ? 'text-[#FF3B30]' :
                        (item.quantity_on_hand || 0) <= (item.reorder_point || 0) ? 'text-[#FF9500]' :
                        'text-[#34C759]'
                      }`} data-testid={`stock-${item.sku}`}>
                        {item.quantity_on_hand || 0}
                      </span>
                    </TableCell>
                    <TableCell className="text-right font-mono">J${(item.cost_price || item.avg_cost || 0).toFixed(2)}</TableCell>
                    <TableCell className="text-right font-mono">J${(item.selling_price || 0).toFixed(2)}</TableCell>
                    <TableCell>
                      <Badge className={item.status === 'active' ? 'bg-[#34C759]/10 text-[#34C759]' : 'bg-[#0A0A0A]/10 text-[#0A0A0A]/60'}>
                        {item.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          onClick={() => {
                            setSelectedItemForBarcode(item);
                            setBarcodeDialogOpen(true);
                          }}
                          title="View Barcode"
                        >
                          <Barcode className="w-4 h-4 text-green-600" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => handleEdit(item)} data-testid={`edit-item-${item.sku}`}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => handleDelete(item.id)} data-testid={`delete-item-${item.sku}`}>
                          <Trash2 className="w-4 h-4 text-[#FF3B30]" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-heading text-xl">
              {editingItem ? 'Edit Item' : 'Add New Item'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>SKU *</Label>
                <Input
                  value={formData.sku}
                  onChange={(e) => setFormData({ ...formData, sku: e.target.value })}
                  placeholder="SKU-001"
                  required
                  disabled={!!editingItem}
                  className="font-mono"
                  data-testid="item-sku-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Unit of Measure</Label>
                <Select value={formData.unit_of_measure} onValueChange={(v) => setFormData({ ...formData, unit_of_measure: v })}>
                  <SelectTrigger data-testid="item-uom-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="EA">EA (Each)</SelectItem>
                    <SelectItem value="PCS">PCS (Pieces)</SelectItem>
                    <SelectItem value="KG">KG (Kilograms)</SelectItem>
                    <SelectItem value="LB">LB (Pounds)</SelectItem>
                    <SelectItem value="M">M (Meters)</SelectItem>
                    <SelectItem value="FT">FT (Feet)</SelectItem>
                    <SelectItem value="BOX">BOX</SelectItem>
                    <SelectItem value="CASE">CASE</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Product Name"
                required
                data-testid="item-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Product description..."
                rows={2}
                data-testid="item-description-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Category</Label>
                <Input
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  placeholder="Electronics"
                  data-testid="item-category-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Barcode</Label>
                <Input
                  value={formData.barcode}
                  onChange={(e) => setFormData({ ...formData, barcode: e.target.value })}
                  placeholder="1234567890"
                  className="font-mono"
                  data-testid="item-barcode-input"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Cost Price (J$)</Label>
                <Input
                  type="number"
                  value={formData.cost_price}
                  onChange={(e) => setFormData({ ...formData, cost_price: parseFloat(e.target.value) || 0 })}
                  placeholder="0.00"
                  min="0"
                  step="0.01"
                  className="font-mono"
                  data-testid="item-cost-price-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Selling Price (J$)</Label>
                <Input
                  type="number"
                  value={formData.selling_price}
                  onChange={(e) => setFormData({ ...formData, selling_price: parseFloat(e.target.value) || 0 })}
                  placeholder="0.00"
                  min="0"
                  step="0.01"
                  className="font-mono"
                  data-testid="item-selling-price-input"
                />
              </div>
            </div>
            {!editingItem && (
              <div className="space-y-2">
                <Label>Starting Quantity</Label>
                <Input
                  type="number"
                  value={formData.initial_quantity}
                  onChange={(e) => setFormData({ ...formData, initial_quantity: parseInt(e.target.value) || 0 })}
                  placeholder="0"
                  min="0"
                  className="font-mono"
                  data-testid="item-quantity-input"
                />
                <p className="text-xs text-[#0A0A0A]/40">Set the initial stock quantity for this item</p>
              </div>
            )}
            {editingItem && (
              <div className="space-y-2">
                <Label>Set Stock Quantity</Label>
                <Input
                  type="number"
                  value={formData.set_quantity ?? ''}
                  onChange={(e) => setFormData({ ...formData, set_quantity: e.target.value === '' ? '' : parseInt(e.target.value) || 0 })}
                  placeholder="Leave blank to keep current"
                  min="0"
                  className="font-mono"
                  data-testid="item-set-quantity-input"
                />
                <p className="text-xs text-[#0A0A0A]/40">Enter a number to set/update stock level for this item</p>
              </div>
            )}
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Reorder Point</Label>
                <Input
                  type="number"
                  value={formData.reorder_point}
                  onChange={(e) => setFormData({ ...formData, reorder_point: parseFloat(e.target.value) || 0 })}
                  min="0"
                  data-testid="item-reorder-point-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Reorder Qty</Label>
                <Input
                  type="number"
                  value={formData.reorder_qty}
                  onChange={(e) => setFormData({ ...formData, reorder_qty: parseFloat(e.target.value) || 0 })}
                  min="0"
                  data-testid="item-reorder-qty-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Safety Stock</Label>
                <Input
                  type="number"
                  value={formData.safety_stock}
                  onChange={(e) => setFormData({ ...formData, safety_stock: parseFloat(e.target.value) || 0 })}
                  min="0"
                  data-testid="item-safety-stock-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-[#007AFF] hover:bg-[#0066D6]" data-testid="save-item-btn">
                {editingItem ? 'Update' : 'Create'} Item
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      {/* Single Item Barcode Dialog */}
      <Dialog open={barcodeDialogOpen} onOpenChange={setBarcodeDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Item Barcode</DialogTitle>
          </DialogHeader>
          {selectedItemForBarcode && (
            <div className="flex flex-col items-center py-4">
              <BarcodeLabel 
                sku={selectedItemForBarcode.sku}
                name={selectedItemForBarcode.name}
                price={selectedItemForBarcode.avg_cost}
                width={280}
              />
              <div className="mt-4 flex gap-2">
                <Button 
                  onClick={() => {
                    setSelectedItems([selectedItemForBarcode]);
                    setShowBatchPrint(true);
                    setBarcodeDialogOpen(false);
                  }}
                  className="bg-green-600 hover:bg-green-700"
                >
                  <Printer className="w-4 h-4 mr-2" />
                  Print Label
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Batch Print View */}
      {showBatchPrint && selectedItems.length > 0 && (
        <BarcodePrintView 
          items={selectedItems.map(item => ({
            id: item.id,
            sku: item.sku,
            name: item.name,
            price: item.avg_cost
          }))}
          onClose={() => {
            setShowBatchPrint(false);
            setSelectedItems([]);
          }}
        />
      )}
    </div>
  );
}
